import { IAssetResponse, ITreeItemResponse } from '@kcftech/prometheus-client';
import {
    Dropdown,
    DropdownItemRenderer,
    DropdownLabelRenderer,
} from 'components/dropdown/Dropdown';
import { useInfiniteAssetDataSource } from 'custom_hooks/data_hooks/data_source/assetsDataSource';
import { useValueAsTuple } from 'custom_hooks/useValueAsTuple';
import { useEffect } from 'react';
import { getId } from 'utils/data';
import { buildTooltip, buildTooltipExcludingGroups } from 'utils/group_hierarchy';

const renderLabel: DropdownLabelRenderer<IAssetResponse> = ({ value }) =>
    value ? buildTooltip(value.Hierarchy, undefined, value.Name) : 'Select an Asset';

export type AssetDropdownProps = Readonly<{
    corporationId?: string;
    locationId?: string;
    /**
     * The parent group ID to fetch assets from.
     */
    parentGroupId?: string;
    value: IAssetResponse | undefined;
    onChange: (newValue: IAssetResponse) => void;
    /**
     * Callback when we select an option from the dropdown
     */
    onDropdownSelect?: () => void;

    groupTreeData?: ITreeItemResponse | undefined;
}>;

export function AssetDropdown({
    corporationId,
    locationId,
    parentGroupId,
    value,
    onChange,
    onDropdownSelect,
    groupTreeData,
}: AssetDropdownProps) {
    const groupIds = useValueAsTuple(parentGroupId);

    const {
        filter,
        setFilter,
        results,
        isLoading,
        hasNext,
        loadNext,
    } = useInfiniteAssetDataSource({
        name: 'asset-dropdown',
        pageLimit: 25,
        defaultFilter: {
            corporationId,
            locationId,
            groupIds,
        },
    });

    useEffect(
        () =>
            setFilter((current) => ({ ...current, corporationId, locationId, groupIds })),
        [corporationId, groupIds, locationId, setFilter],
    );

    const renderItem: DropdownItemRenderer<IAssetResponse> = ({ item }) => {
        if (locationId === undefined) {
            // if the location is undefined, we don't have the name in the breadcrumb so we want to include it as a prefix
            return buildTooltip(item.Hierarchy, item.Location.LocationName, item.Name);
        }
        if (groupIds === undefined) {
            return buildTooltip(item.Hierarchy, undefined, item.Name);
        }
        const excludedGroupIds = groupTreeData
            ? groupTreeData.Hierarchy.map((h) => h.HierarchyId)
            : [];
        return buildTooltipExcludingGroups(
            item.Hierarchy,
            [...excludedGroupIds, ...groupIds],
            item.Name,
        );
    };

    return (
        <Dropdown<IAssetResponse>
            dataKey={getId}
            data={{
                items: results,
                isLoading,
                hasMore: hasNext ?? false,
                onLoadMore: loadNext,
            }}
            value={value}
            renderLabel={renderLabel}
            renderItem={renderItem}
            filterable={{
                placeholder: 'Search Assets',
                filterText: filter.nameSearch ?? '',
                onChange: (nameSearch) =>
                    setFilter((current) => ({ ...current, nameSearch })),
            }}
            onChange={(newValue) => {
                onDropdownSelect?.();
                onChange(newValue);
            }}
        />
    );
}
